import React, { useState } from 'react';
import { List, useTable, EditButton, ShowButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Input, Tag, Button, Modal, message } from 'antd';
import { SearchOutlined, CloudUploadOutlined } from '@ant-design/icons';
import { useInvalidate } from '@refinedev/core';
import { API_BASE_URL, axiosInstance } from '../../providers/dataProvider';
import { MissingTranslationsButton } from '../../components/MissingTranslationsButton';

interface AppStringDto {
  id: string;
  key: string;
  namespace: string;
  description?: string | null;
  defaultText: string;
  translations?: Record<string, { text?: string }> | null;
  updatedAt: string;
}

export const AppStringList: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [publishing, setPublishing] = useState(false);
  const invalidate = useInvalidate();

  const { tableProps } = useTable<AppStringDto>({
    resource: 'app-strings',
    filters: {
      permanent: [{ field: 'search', operator: 'contains', value: searchText }],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  const runPublish = async () => {
    setPublishing(true);
    try {
      const { data } = await axiosInstance.post(
        `${API_BASE_URL}/admin/lang-packs/publish`,
        { notes: 'Published from CMS' },
      );
      message.success(`Published lang pack v${data?.version ?? '?'}`);
      invalidate({ resource: 'lang-pack-releases', invalidates: ['list'] });
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || err?.message || 'Publish failed',
      );
    } finally {
      setPublishing(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    let missingCount = 0;
    try {
      const { data } = await axiosInstance.get(
        `${API_BASE_URL}/admin/app-strings/missing-translations`,
      );
      missingCount = Number(data?.missingCount ?? 0) || 0;
    } catch {
      // Still allow publish; warn that the gap check failed.
      message.warning('Could not check missing translations; proceed carefully.');
    } finally {
      setPublishing(false);
    }

    Modal.confirm({
      title:
        missingCount > 0
          ? 'Publish with missing translations?'
          : 'Publish lang pack?',
      content:
        missingCount > 0
          ? `${missingCount} string(s) are missing translations and will fall back to English in the published pack. Continue?`
          : 'Creates a new version from all current app strings. The app will pull this on next sync. Draft edits stay off OTA until this Publish.',
      okText: 'Publish',
      okButtonProps: missingCount > 0 ? { danger: true } : undefined,
      onOk: () => runPublish(),
    });
  };

  return (
    <List
      resource="app-strings"
      headerButtons={({ defaultButtons }) => (
        <>
          {defaultButtons}
          <MissingTranslationsButton
            endpoint="admin/app-strings/missing-translations"
            resourceLabel="App Strings"
          />
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            loading={publishing}
            onClick={handlePublish}
          >
            Publish Lang Pack
          </Button>
        </>
      )}
    >
      <div style={{ marginBottom: 16 }}>
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Search by key or text..."
          defaultValue={searchText}
          onSearch={(value) => setSearchText(value)}
          allowClear
          style={{ width: 360 }}
        />
      </div>
      <Table {...tableProps} rowKey="id" scroll={{ x: 1000 }}>
        <Table.Column dataIndex="namespace" title="Namespace" width={120} />
        <Table.Column dataIndex="key" title="Key" />
        <Table.Column dataIndex="defaultText" title="EN Text" ellipsis />
        <Table.Column
          title="Locales"
          render={(_: unknown, record: AppStringDto) => {
            const locales = Object.keys(record.translations ?? {});
            return locales.length
              ? locales.map((code) => <Tag key={code}>{code}</Tag>)
              : '—';
          }}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_: unknown, record: AppStringDto) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.id} resource="app-strings" />
              <ShowButton hideText size="small" recordItemId={record.id} resource="app-strings" />
              <DeleteButton hideText size="small" recordItemId={record.id} resource="app-strings" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
